/* eslint-disable no-implicit-coercion */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/jsx-curly-brace-presence */
import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { Button, token } from '@bynder/design-system';
import { IconArrowLeft, IconArrowRight, IconCancel } from '@bynder/icons';
import { lazyPrefetching } from 'packages/helpers/helpers';
import * as persistVolume from 'packages/helpers/persistVolume';
import Loader from 'packages/common/loader/Loader';
import {
    PlayerBackdrop,
    PlayerCloseButton,
    PlayerLoading,
    PlayerMedia,
    PlayerRoot,
    PlayerStage,
} from './Player.styled';

export const VideoPlayer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "videojs" */
            /* webpackPrefetch: true */
            './Video'
        ),
);

export const AudioPlayer = lazyPrefetching(
    () =>
        import(
            /* webpackChunkName: "videojs" */
            /* webpackPrefetch: true */
            './Audio'
        ),
);

interface Props {
    src?: string;
    poster?: string;
    type?: string;
    carousel?: boolean;
    disabled?: any;
    prev?: any;
    next?: any;
    download?: any;
    onlyPoster?: boolean;
    alwaysFullScreen?: boolean;
    handleToggle?: () => void;
    [key: string]: any;
}

export default function Player(props: Props) {
    const {
        src,
        poster,
        type,
        carousel,
        disabled,
        prev,
        next,
        download,
        onlyPoster = false,
        alwaysFullScreen,
        handleToggle,
    } = props;

    const [fullScreen, setFullScreen] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const volumeRef = useRef(+(persistVolume.getVolume() ?? 0.25));
    const isMutedRef = useRef(persistVolume.getMutedStatus());
    const isUnMountedRef = useRef(false);
    const fullScreenRef = useRef(false);

    const isFullScreen = alwaysFullScreen || fullScreen;
    fullScreenRef.current = isFullScreen;

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!fullScreenRef.current) return;
            if (e.keyCode === 37) {
                prev();
            }
            if (e.keyCode === 39) {
                next();
            }
        },
        [prev, next],
    );

    useEffect(() => {
        return () => {
            isUnMountedRef.current = true;
        };
    }, []);

    useEffect(() => {
        if (carousel) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [carousel, handleKeyDown]);

    useEffect(() => {
        setLoaded(false);
    }, [src]);

    const toggleFullScreen = useCallback(() => {
        const toggle =
            handleToggle ||
            (() => {
                setFullScreen((prev) => !prev);
            });
        toggle();
    }, [handleToggle]);

    const clickOutside = useCallback(
        (e: React.MouseEvent) => {
            if (e.target === e.currentTarget) {
                toggleFullScreen();
            }
        },
        [toggleFullScreen],
    );

    const handleLoad = useCallback(() => {
        if (isUnMountedRef.current) return;
        setLoaded(true);
    }, []);

    const handleVolumeChange = useCallback((volume, muted) => {
        persistVolume.setVolume(volume);
        persistVolume.setMutedStatus(muted);
    }, []);

    const showNextPrev = isFullScreen && carousel;

    return (
        <>
            {isFullScreen && <PlayerBackdrop />}
            <PlayerRoot>
                <PlayerStage $isFullScreen={isFullScreen} onClick={clickOutside}>
                    {(!loaded || !src) && (
                        <PlayerLoading>
                            <Loader poster={poster} size={50} />
                            {!onlyPoster && <small>Loading media</small>}
                        </PlayerLoading>
                    )}
                    {isFullScreen && (
                        <PlayerCloseButton>
                            <Button
                                title="Close"
                                variant="clean"
                                onClick={toggleFullScreen}
                                icon={<IconCancel style={{ color: token.white }} />}
                            />
                        </PlayerCloseButton>
                    )}
                    {showNextPrev && (
                        <div
                            onClick={prev}
                            className={`fullscreen-arrow fullscreen-arrow-prev ${
                                disabled.prevButton ? 'disabled' : ''
                            }`}
                        >
                            <IconArrowLeft />
                        </div>
                    )}

                    {src && (
                        <PlayerMedia $loaded={loaded} $isFullScreen={isFullScreen}>
                            {type === 'VIDEO' && (
                                <Suspense fallback={<div>Loading...</div>}>
                                    <VideoPlayer
                                        key={src}
                                        {...props}
                                        onFullScreen={toggleFullScreen}
                                        onPlayerReady={handleLoad}
                                        volume={volumeRef.current}
                                        isMuted={isMutedRef.current}
                                        onVolumeChange={handleVolumeChange}
                                    />
                                </Suspense>
                            )}
                            {type === 'AUDIO' && (
                                <Suspense fallback={<div>Loading...</div>}>
                                    <AudioPlayer
                                        key={src}
                                        {...props}
                                        onFullScreen={toggleFullScreen}
                                        onPlayerReady={handleLoad}
                                        volume={volumeRef.current}
                                        isMuted={isMutedRef.current}
                                        onVolumeChange={handleVolumeChange}
                                    />
                                </Suspense>
                            )}
                        </PlayerMedia>
                    )}
                    {showNextPrev && (
                        <div
                            onClick={next}
                            className={`fullscreen-arrow fullscreen-arrow-next ${
                                disabled.nextButton ? 'disabled' : ''
                            }`}
                        >
                            <IconArrowRight />
                        </div>
                    )}
                </PlayerStage>
            </PlayerRoot>
        </>
    );
}
